import type { ReadonlyVec3 } from 'gl-matrix';
import { Overlay, type OverlayQuad } from './Overlay';

/**
 * Collects every {@link Overlay} attached to a scene. {@link OverlayPass} pulls
 * the per-frame quad list out via {@link collect}. Lifetime mirrors
 * {@link Scene} — destroyed via {@link clear} on detach.
 */
export class OverlayManager {
  private readonly overlays = new Set<Overlay>();

  public add(overlay: Overlay): void {
    this.overlays.add(overlay);
  }

  public remove(overlay: Overlay): void {
    if (this.overlays.delete(overlay)) {
      overlay.destroy?.();
    }
  }

  /**
   * Tick + emit. The caller passes a scratch list; this method appends quads
   * to it. Reaps expired overlays after collection. Applies per-overlay
   * distance fade + cull driven by `maxDistance` / `fadeDistance` — overlays
   * beyond the fade band are culled entirely, those inside it have an
   * `alphaScale` stamped on every quad they emit.
   */
  public collect(out: OverlayQuad[], dt: number, cameraPos: ReadonlyVec3 | number[]): void {
    let expiredHits = 0;
    const cx = cameraPos[0];
    const cy = cameraPos[1];
    const cz = cameraPos[2];

    for (const overlay of this.overlays) {
      overlay.update?.(dt);
      if (overlay.expired) {
        expiredHits++;
        continue;
      }
      if (!overlay.visible) continue;

      // Cull-distance check uses the anchor (tracker overrides field). We
      // intentionally measure to the anchor, not to individual emitted
      // quads — they all share the anchor so one decision per overlay
      // suffices and stays consistent under tracker motion.
      const anchor = overlay.tracker ? overlay.tracker.position : overlay.worldPosition;
      const dx = anchor[0] - cx;
      const dy = anchor[1] - cy;
      const dz = anchor[2] - cz;
      const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
      const maxD = overlay.maxDistance;
      const fadeD = overlay.fadeDistance;
      if (dist >= maxD + fadeD) continue;

      const fade = dist <= maxD ? 1 : 1 - (dist - maxD) / fadeD;

      const startIdx = out.length;
      overlay.emit(out);
      if (fade < 1) {
        for (let i = startIdx; i < out.length; i++) {
          out[i].alphaScale = (out[i].alphaScale ?? 1) * fade;
        }
      }
    }
    // Reap in a second pass to avoid mutating the set during iteration.
    if (expiredHits > 0) {
      for (const overlay of this.overlays) {
        if (overlay.expired) {
          this.overlays.delete(overlay);
          overlay.destroy?.();
        }
      }
    }
  }

  public get size(): number {
    return this.overlays.size;
  }

  public clear(): void {
    for (const overlay of this.overlays) overlay.destroy?.();
    this.overlays.clear();
  }
}
